import React from "react";
import { FaCheck, FaTimes, FaEdit } from "react-icons/fa";
import {
  IconButton,
  SoftButton,
  ToggleSwitch,
} from "../UI/DesignStandards";
import CatalogAverageNotice from "./CatalogAverageNotice";

const MobileBowlerCard = React.memo(
  ({
    bowler,
    isEditing,
    editedAverage,
    editedHasAverage,
    errorMessage,
    canManage,
    verifyingBowlerId,
    verifyingAll,
    onEditClick,
    onAverageChange,
    onHasAverageChange,
    onSaveClick,
    onCancelClick,
    onVerifyClick,
    canVerify,
    canUnverify,
    onUseCatalogAverage,
    onReview,
  }) => {
    const handleVerifyToggle = () => {
      onVerifyClick(bowler.bowlerId, bowler.isVerified);
    };
    const verifyBusy =
      verifyingAll || verifyingBowlerId === bowler.bowlerId;

    return (
      <div className="card-inset p-3">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="heading-display text-base truncate">
              {bowler.bowlerName}
            </h3>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-sm font-score text-brand-charcoal">
                {bowler.hasAverage === false ? "No Average" : bowler.average}
              </span>
              <span
                className={`text-xs font-display font-bold ${
                  bowler.isVerified ? "text-brand-charcoal" : "text-brand-muted"
                }`}
              >
                {bowler.isVerified ? "Verified" : "Not verified"}
              </span>
            </div>
          </div>
          {canManage && !bowler.isVerified && !isEditing ? (
            <IconButton
              icon={FaEdit}
              variant="secondary"
              size="sm"
              onClick={() => onEditClick(bowler)}
              title="Edit average"
            />
          ) : null}
        </div>

        {isEditing ? (
          <div className="space-y-3 mt-3">
            <div className="flex items-center space-x-2">
              <ToggleSwitch
                checked={editedHasAverage}
                onChange={(checked) => onHasAverageChange(checked)}
                disabled={bowler.isVerified}
                size="sm"
              />
              <span className="text-sm text-brand-muted">
                {editedHasAverage ? "Has Average" : "No Average"}
              </span>
            </div>
            {editedHasAverage && (
              <input
                type="number"
                min="0"
                max="300"
                value={editedAverage}
                onChange={onAverageChange}
                className="w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg font-score bg-white text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
                placeholder="Enter average"
              />
            )}
            {errorMessage && (
              <p className="text-sm text-red-600">{errorMessage}</p>
            )}
            <div className="flex space-x-2">
              <IconButton
                icon={FaCheck}
                variant="success"
                size="sm"
                onClick={() => onSaveClick(bowler.bowlerId)}
              />
              <IconButton
                icon={FaTimes}
                variant="danger"
                size="sm"
                onClick={onCancelClick}
              />
            </div>
          </div>
        ) : null}

        {canManage && (canVerify || canUnverify) ? (
          <div className="flex space-x-2 mt-3">
            {canVerify && onReview ? (
              <SoftButton
                size="xs"
                onClick={() => onReview(bowler.bowlerId)}
                disabled={verifyBusy}
                className="flex-1 !rounded-lg"
              >
                Review
              </SoftButton>
            ) : null}
            {canVerify && (
              <SoftButton
                color="gold"
                size="xs"
                onClick={handleVerifyToggle}
                disabled={verifyBusy}
                iconLeft={
                  verifyingBowlerId === bowler.bowlerId ? (
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
                  ) : (
                    <FaCheck className="h-3 w-3" />
                  )
                }
                className="flex-1 !rounded-lg"
                title="Verify average"
              >
                {verifyingBowlerId === bowler.bowlerId
                  ? "Verifying..."
                  : "Verify"}
              </SoftButton>
            )}
            {canUnverify && (
              <SoftButton
                color="red"
                size="xs"
                onClick={handleVerifyToggle}
                disabled={verifyBusy || bowler.hasVerifiedTeamScores}
                iconLeft={
                  verifyingBowlerId === bowler.bowlerId ? (
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
                  ) : (
                    <FaTimes className="h-3 w-3" />
                  )
                }
                className={`flex-1 !rounded-lg ${
                  bowler.hasVerifiedTeamScores
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
                title={
                  bowler.hasVerifiedTeamScores
                    ? "Cannot unverify: Bowler is on teams with verified scores"
                    : "Unverify average"
                }
              >
                {verifyingBowlerId === bowler.bowlerId
                  ? "Unverifying..."
                  : "Unverify"}
              </SoftButton>
            )}
          </div>
        ) : null}
        <CatalogAverageNotice
          bowler={bowler}
          canManage={canManage}
          verifying={verifyBusy}
          onUseCatalogAverage={onUseCatalogAverage}
        />
      </div>
    );
  }
);

MobileBowlerCard.displayName = "MobileBowlerCard";

export default MobileBowlerCard;
